// How the book of business has moved, month by month.
//
// The rows are `GET /api/reports/trends`, and the screen's whole job is to keep
// its clocks APART. A signing, a bill, a document, a payment and a run
// rate happen on different days for the same money, and a chart that put them
// on one line would invite the reader to add them up. So each clock gets its
// own card, its own y-axis and its own sentence saying what it counts, and the
// one table at the bottom carries every figure the charts draw — the chart is
// an enrichment, the table is the record.
//
// TWO THINGS THIS SCREEN REFUSES TO DO. It never draws a count and an amount
// on one plot (the deals card is counts, the bookings card is money), and it
// never totals the run rate: twelve months of MRR add up to nothing, so the
// tile shows the latest month and the totals row leaves the column blank.
//
// TO BILL is the month close's own figure for each month — what is owed and not
// on an issued document yet — so a window nobody has invoiced in still shows
// the money it is owed, rather than a row of empty charts.
//
// AND ONE THING IT SAYS OUT LOUD. An our-share figure skips rows whose split
// does not resolve; the report counts what it skipped, and the chart marks the
// month so a dip reads as "unknown" rather than "down".

import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Currency } from "@crm/shared";
import type { MarginTrendReport, Partner, ReportScope, TrendsReport, TrendsRow } from "@crm/shared";
import styles from "./TrendsPage.module.css";
import { searchToTrendsState, trendsStateToSearch, type TrendsSearch } from "./trendsSearch.js";
import { MonthlyChart, type ChartSeries } from "../../design/chart/MonthlyChart.js";
import { Card, CardBody, CardHeader } from "../../design/Card.js";
import { FxFootnote } from "../../design/FxFootnote.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { Segmented, type SegmentedOption } from "../../design/Segmented.js";
import { StatTile, StatTileRow } from "../../design/StatTile.js";
import { EmptyRow, Table, Td, Th } from "../../design/Table.js";
import { usePageChrome } from "../../design/pageChrome.js";
import { formatMonth, todayIsoDate } from "../../format/calendarDate.js";
import { useReportCurrency } from "../../format/reportCurrency.js";
import { useDebouncedValue } from "../../lib/useDebouncedValue.js";
import { formatCount, formatMoney, formatPercent } from "../../format/money.js";
import {
  usePartnerOptions,
  useMarginTrend,
  useTrendsReport,
  type TrendsQueryState,
  type TrendsWindowMonths,
} from "../../api/queries.js";

const ANY_PARTNER = "__any__";

const WINDOWS: ReadonlyArray<SegmentedOption<TrendsWindowMonths>> = [
  { value: 12, label: "12m", title: "The trailing twelve months" },
  { value: 24, label: "24m", title: "The trailing two years" },
  { value: 36, label: "36m", title: "The trailing three years" },
];

const SCOPES: ReadonlyArray<SegmentedOption<ReportScope>> = [
  { value: "direct", label: "This company" },
  { value: "rolled_up", label: "Including owned companies" },
];

export function TrendsPage(): ReactNode {
  const search = useSearch({ from: "/trends" });
  const navigate = useNavigate();
  const reportCurrency = useReportCurrency();
  const state = useMemo(
    () => searchToTrendsState(search, reportCurrency),
    [search, reportCurrency],
  );
  // Read once: the window ends in the month the page was opened in, so a page
  // left open over midnight on the last of the month does not refetch itself
  // into a different window under the reader.
  const [thisMonth] = useState(() => todayIsoDate().slice(0, 7));

  const setState = useCallback(
    (next: TrendsQueryState) => {
      void navigate({
        to: "/trends",
        search: trendsStateToSearch(next) as TrendsSearch,
      });
    },
    [navigate],
  );

  // The controls move the URL at once; the reports follow once they hold still,
  // so clicking through the windows asks for the last one only.
  const asked = useDebouncedValue(state);
  const report = useTrendsReport(asked, thisMonth);
  // Asked for AFTER the trends report, not beside it: both work out the open
  // month's statements, the slowest thing the API does, and two at once only
  // makes each slower. The margin cards sit below the fold anyway.
  const margin = useMarginTrend(
    {
      toMonth: thisMonth,
      months: asked.months,
      currency: asked.currency,
      partnerId: asked.partnerId,
      scope: asked.scope,
    },
    { enabled: !report.isFetching },
  );
  const partners = usePartnerOptions();

  usePageChrome({
    title: "Trends",
  });

  return (
    <div className={styles.page}>
      <HeadlineTiles report={report.data} />

      <div className={styles.toolbar}>
        <Segmented
          label="Window"
          variant="inset"
          options={WINDOWS}
          value={state.months}
          onChange={(months) => setState({ ...state, months })}
        />
        <PartnerFilter
          partners={partners.data?.items ?? []}
          partnerId={state.partnerId}
          onChange={(partnerId) => setState({ ...state, partnerId })}
        />
        {state.partnerId !== null && (
          <Segmented
            label="Partner scope"
            variant="inset"
            options={SCOPES}
            value={state.scope}
            onChange={(scope) => setState({ ...state, scope })}
          />
        )}
      </div>

      <QueryBoundary query={report} label="the trends report">
        {(data) => <TrendsBody report={data} />}
      </QueryBoundary>

      <QueryBoundary query={margin} label="the margin trend">
        {(data) => <MarginTrends report={data} />}
      </QueryBoundary>
    </div>
  );
}

function PartnerFilter({
  partners,
  partnerId,
  onChange,
}: {
  partners: readonly Partner[];
  partnerId: string | null;
  onChange: (partnerId: string | null) => void;
}): ReactNode {
  return (
    <select
      className={styles.control}
      aria-label="Partner"
      value={partnerId ?? ANY_PARTNER}
      onChange={(event) => {
        const picked = event.target.value;
        onChange(picked === ANY_PARTNER ? null : picked);
      }}
    >
      <option value={ANY_PARTNER}>Every partner</option>
      {partners.map((partner) => (
        <option key={partner.id} value={partner.id}>
          {partner.name}
        </option>
      ))}
    </select>
  );
}

/**
 * The five figures above the fold: what was signed, billed, still to bill and
 * collected over the window, and the run rate NOW — the latest month, because a run rate is a
 * level and the window's sum of it would be a number that means nothing.
 */
function HeadlineTiles({ report }: { report: TrendsReport | undefined }): ReactNode {
  const latest = report?.rows[report.rows.length - 1];
  const window =
    report === undefined
      ? ""
      : `${formatMonth(report.params.from_month)} – ${formatMonth(report.params.to_month)}`;
  return (
    <StatTileRow>
      <StatTile
        label="Deals won"
        value={report === undefined ? null : formatCount(report.totals.won_deal_count)}
        hint={
          report === undefined
            ? "Won and live, by close date"
            : `${formatMoney(report.totals.won_first_year_our_share_minor, report.currency)} our first-year share · ${formatCount(report.totals.lost_deal_count)} lost · ${window}`
        }
      />
      <StatTile
        label="Billed"
        value={
          report === undefined
            ? null
            : formatMoney(report.totals.billed_our_share_minor, report.currency)
        }
        hint={
          report === undefined || report.totals.billed_unresolved_item_count === 0
            ? "Our share of what was billed, by service month"
            : `Our share, leaving out ${formatCount(report.totals.billed_unresolved_item_count)} bill${report.totals.billed_unresolved_item_count === 1 ? "" : "s"} with no resolvable split`
        }
      />
      <StatTile
        label="To bill"
        value={
          report === undefined ? null : formatMoney(report.totals.to_bill_minor, report.currency)
        }
        hint={
          report === undefined ? "Owed and not invoiced yet, by service month" : toBillHint(report)
        }
      />
      <StatTile
        label="Collected"
        value={
          report === undefined ? null : formatMoney(report.totals.collected_minor, report.currency)
        }
        hint={
          report === undefined
            ? "Paid invoices, by payment date"
            : `${formatMoney(report.totals.invoiced_minor, report.currency)} invoiced over the same window`
        }
      />
      <StatTile
        label="Run rate"
        value={
          latest === undefined || report === undefined
            ? null
            : formatMoney(latest.recurring_our_share_minor, report.currency)
        }
        hint={
          latest === undefined || report === undefined
            ? "Our share of the recurring lines in force, per month"
            : `${formatMonth(latest.month)} · our share across ${formatCount(latest.recurring_line_count)} line${latest.recurring_line_count === 1 ? "" : "s"}`
        }
      />
    </StatTileRow>
  );
}

function toBillHint(report: TrendsReport): string {
  const { to_bill_line_count: lines, to_bill_pending_count: pending } = report.totals;
  if (lines === 0) return "Nothing left to invoice in this window";
  const charges = `${formatCount(lines)} charge${lines === 1 ? "" : "s"} not on an invoice yet`;
  return pending === 0 ? charges : `${charges}, ${formatCount(pending)} still waiting on a figure`;
}

/** Axis ticks for money: whole units, no cents, no code — the card says the currency once. */
function tickMoney(currency: Currency): (minor: number) => string {
  return (minor) => {
    const major = Math.trunc(minor / 100);
    const abs = Math.abs(major);
    const sign = major < 0 ? "-" : "";
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1).replace(/\.0$/, "")}m`;
    if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
    return `${sign}$${abs}`;
  };
  // `currency` is deliberately unused in the tick: the card names it once.
  void currency;
}

/** The revenue chart's notes: bills with no split, and charges still to bill with no figure. */
function revenueCaveats(rows: readonly TrendsRow[]): (string | null)[] {
  const unresolved = caveatsFor(rows, (r) => r.billed_unresolved_item_count, "bill");
  return rows.map((row, index) => {
    const pending = row.to_bill_pending_count;
    const notes = [
      unresolved[index],
      pending === 0
        ? null
        : `${formatCount(pending)} charge${pending === 1 ? "" : "s"} still to bill waiting on a figure`,
    ].filter((note): note is string => note !== null);
    return notes.length === 0 ? null : notes.join("; ");
  });
}

function caveatsFor(
  rows: readonly TrendsRow[],
  count: (row: TrendsRow) => number,
  noun: string,
): (string | null)[] {
  return rows.map((row) => {
    const n = count(row);
    return n === 0
      ? null
      : `${formatCount(n)} ${noun}${n === 1 ? "" : "s"} could not resolve a split — left out of our share`;
  });
}

/**
 * The book's margin month over month, from the margin report. Customers with
 * no cost on file are left out of every figure here, as they are out of the
 * margin page's totals; a month where none has a cost has no margin at all,
 * which is drawn as a gap in the words, not as a zero anybody earned.
 */
function MarginTrends({ report }: { report: MarginTrendReport }): ReactNode {
  const months = report.book.map((point) => point.period);
  const currency = report.currency;
  const money = (minor: number) => formatMoney(minor, currency, { omitCurrencyCode: true });
  const tick = tickMoney(currency);
  const unknown = report.book.map((point) =>
    point.margin_pct === null ? "No customer had a cost on file" : null,
  );

  const marginSeries: ChartSeries[] = [
    {
      key: "margin",
      label: "Margin",
      kind: "line",
      slot: 1,
      values: report.book.map((point) => point.margin_pct ?? 0),
    },
  ];
  // Revenue here is the costed customers' only, so the bars and the line share
  // one set of customers: contribution + what we carry.
  const contribution: ChartSeries[] = [
    {
      key: "revenue",
      label: "Our recurring revenue",
      kind: "bar",
      slot: 1,
      values: report.book.map(
        (point) => (point.contribution_minor ?? 0) + (point.our_cost_minor ?? 0),
      ),
    },
    {
      key: "cost",
      label: "Cost we carry",
      kind: "bar",
      slot: 3,
      values: report.book.map((point) => point.our_cost_minor ?? 0),
    },
    {
      key: "contribution",
      label: "Contribution",
      kind: "line",
      slot: 2,
      values: report.book.map((point) => point.contribution_minor ?? 0),
    },
  ];

  return (
    <div className={styles.grid}>
      <Card>
        <CardHeader
          title="Margin"
          description="Contribution over the recurring revenue that reaches us, per month. One-time fees are left out, and so are customers with no cost on file."
        />
        <CardBody>
          <MonthlyChart
            label="Book margin by month"
            months={months}
            series={marginSeries}
            formatValue={(value) => formatPercent(value, 1)}
            formatTick={(value) => formatPercent(value, 0)}
            caveats={unknown}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Contribution"
          description={`Our recurring revenue less the cost we carry, in ${currency}, for the customers with a cost on file. Per customer, see the margin page.`}
        />
        <CardBody>
          <MonthlyChart
            label="Contribution by month"
            months={months}
            series={contribution}
            formatValue={money}
            formatTick={tick}
            caveats={unknown}
          />
        </CardBody>
      </Card>
    </div>
  );
}

function TrendsBody({ report }: { report: TrendsReport }): ReactNode {
  const months = report.rows.map((row) => row.month);
  const currency = report.currency;
  const money = (minor: number) => formatMoney(minor, currency, { omitCurrencyCode: true });
  const tick = tickMoney(currency);
  const nothing = report.rows.every(
    (row) =>
      row.won_deal_count === 0 &&
      row.lost_deal_count === 0 &&
      row.billed_item_count === 0 &&
      row.invoiced_invoice_count === 0 &&
      row.collected_invoice_count === 0 &&
      row.to_bill_line_count === 0 &&
      row.recurring_line_count === 0 &&
      row.cost_item_count === 0,
  );

  const deals: ChartSeries[] = [
    {
      key: "won",
      label: "Won",
      kind: "bar",
      slot: 1,
      values: report.rows.map((r) => r.won_deal_count),
    },
    {
      key: "lost",
      label: "Lost",
      kind: "bar",
      slot: 3,
      values: report.rows.map((r) => r.lost_deal_count),
    },
  ];
  const bookings: ChartSeries[] = [
    {
      key: "share",
      label: "Our first-year share",
      kind: "bar",
      slot: 1,
      values: report.rows.map((r) => r.won_first_year_our_share_minor),
    },
  ];
  const revenue: ChartSeries[] = [
    {
      key: "billed",
      label: "Billed (our share)",
      kind: "line",
      slot: 1,
      values: report.rows.map((r) => r.billed_our_share_minor),
    },
    {
      key: "toBill",
      label: "Still to bill",
      kind: "line",
      slot: 1,
      dashed: true,
      values: report.rows.map((r) => r.to_bill_minor),
    },
    {
      key: "invoiced",
      label: "Invoiced",
      kind: "line",
      slot: 2,
      values: report.rows.map((r) => r.invoiced_minor),
    },
    {
      key: "collected",
      label: "Collected",
      kind: "line",
      slot: 3,
      values: report.rows.map((r) => r.collected_minor),
    },
  ];
  const runRate: ChartSeries[] = [
    {
      key: "share",
      label: "Our share",
      kind: "line",
      slot: 1,
      values: report.rows.map((r) => r.recurring_our_share_minor),
    },
  ];
  const costs: ChartSeries[] = [
    {
      key: "absorbed",
      label: "Absorbed",
      kind: "bar",
      slot: 3,
      values: report.rows.map((r) => r.cost_absorbed_minor),
    },
    {
      key: "recharged",
      label: "Recharged",
      kind: "bar",
      slot: 1,
      values: report.rows.map((r) => r.cost_recharged_minor),
    },
  ];

  return (
    <>
      {nothing && (
        <div className={styles.empty} role="status">
          Nothing happened in this window — no deal closed, nothing is owed, no bill went out, no
          invoice was issued or paid, and no cost was shared. Widen the window, or check the partner
          filter.
        </div>
      )}

      <div className={styles.grid}>
        <Card>
          <CardHeader
            title="Deals closed"
            description="Won and lost, by the month of each deal's close date. Won counts won and live together: going live is delivery, not a second signing."
          />
          <CardBody>
            <MonthlyChart
              label="Deals closed by month"
              months={months}
              series={deals}
              formatValue={formatCount}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Bookings"
            description={`Our share of the first-year value of the deals won each month, in ${currency} — the pipeline's valuation, landed. Metered lines contribute nothing until usage is recorded.`}
          />
          <CardBody>
            <MonthlyChart
              label="Bookings by month"
              months={months}
              series={bookings}
              formatValue={money}
              formatTick={tick}
              caveats={caveatsFor(report.rows, (r) => r.won_unresolved_deal_count, "deal")}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Revenue"
            description={`Three clocks on the money that reaches us, in ${currency}: our share billed by the month the charge is for, invoiced by issue date, collected by payment date. They are not meant to agree month by month. The dashed line is what each month still has to bill — the To bill screen's figure, by the month the charge is for.`}
          />
          <CardBody>
            <MonthlyChart
              label="Billed, still to bill, invoiced and collected by month"
              months={months}
              series={revenue}
              formatValue={money}
              formatTick={tick}
              caveats={revenueCaveats(report.rows)}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Run rate"
            description={`Our share of every recurring line in force on the first of the month, on a won or live deal, normalised to a month, in ${currency}. A level, not a flow — it is never totalled.`}
          />
          <CardBody>
            <MonthlyChart
              label="Recurring run rate by month"
              months={months}
              series={runRate}
              formatValue={money}
              formatTick={tick}
              caveats={caveatsFor(report.rows, (r) => r.recurring_unresolved_line_count, "line")}
            />
          </CardBody>
        </Card>

        <Card className={styles.wide}>
          <CardHeader
            title="Shared costs"
            description={`Cost recharges by the month the cost is for, in ${currency}: what we absorb, stacked under what goes on a bill. A recharge still waiting on its figure is counted and drawn as nothing — not profit, and not the whole cost of running the business.`}
          />
          <CardBody>
            <MonthlyChart
              label="Shared costs by month"
              months={months}
              series={costs}
              formatValue={money}
              formatTick={tick}
              stacked
              caveats={report.rows.map((r) =>
                r.cost_unpriced_item_count === 0
                  ? null
                  : `${formatCount(r.cost_unpriced_item_count)} recharge${r.cost_unpriced_item_count === 1 ? "" : "s"} still waiting on a figure`,
              )}
            />
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader
          title="Month by month"
          description="Every figure the charts draw, all of it our share. A row whose split does not resolve is left out, and the count beside it says how many."
        />
        <TrendsTable report={report} />
        <div className={styles.footer}>
          <FxFootnote currency={report.currency} fx={report.fx} generatedAt={report.generated_at} />
        </div>
      </Card>
    </>
  );
}

function TrendsTable({ report }: { report: TrendsReport }): ReactNode {
  const currency = report.currency;
  const money = (minor: number) => formatMoney(minor, currency, { omitCurrencyCode: true });
  const skipped = (n: number) =>
    n === 0 ? null : <span className={styles.skipped}> ({formatCount(n)} skipped)</span>;
  return (
    <Table label="Trends by month">
      <thead>
        <tr>
          <Th>Month</Th>
          <Th numeric>Won</Th>
          <Th numeric>Lost</Th>
          <Th numeric>Bookings</Th>
          <Th numeric>Billed</Th>
          <Th numeric>To bill</Th>
          <Th numeric>Invoiced</Th>
          <Th numeric>Collected</Th>
          <Th numeric>Run rate</Th>
          <Th numeric>Absorbed</Th>
          <Th numeric>Recharged</Th>
        </tr>
      </thead>
      <tbody>
        {report.rows.length === 0 ? (
          <EmptyRow
            colSpan={11}
            title="No months in this window"
            description="The window is empty."
          />
        ) : (
          report.rows.map((row) => (
            <tr key={row.month}>
              <Td mono nowrap>
                {formatMonth(row.month)}
              </Td>
              <Td numeric>{formatCount(row.won_deal_count)}</Td>
              <Td numeric>{formatCount(row.lost_deal_count)}</Td>
              <Td numeric mono>
                {money(row.won_first_year_our_share_minor)}
                {skipped(row.won_unresolved_deal_count)}
              </Td>
              <Td numeric mono>
                {money(row.billed_our_share_minor)}
                {skipped(row.billed_unresolved_item_count)}
              </Td>
              <Td numeric mono>
                {money(row.to_bill_minor)}
                {row.to_bill_pending_count === 0 ? null : (
                  <span className={styles.skipped}>
                    {" "}
                    ({formatCount(row.to_bill_pending_count)} unpriced)
                  </span>
                )}
              </Td>
              <Td numeric mono>
                {money(row.invoiced_minor)}
              </Td>
              <Td numeric mono>
                {money(row.collected_minor)}
              </Td>
              <Td numeric mono>
                {money(row.recurring_our_share_minor)}
                {skipped(row.recurring_unresolved_line_count)}
              </Td>
              <Td numeric mono>
                {money(row.cost_absorbed_minor)}
              </Td>
              <Td numeric mono>
                {money(row.cost_recharged_minor)}
                {row.cost_unpriced_item_count === 0 ? null : (
                  <span className={styles.skipped}>
                    {" "}
                    ({formatCount(row.cost_unpriced_item_count)} unpriced)
                  </span>
                )}
              </Td>
            </tr>
          ))
        )}
      </tbody>
      <tfoot>
        <tr>
          <Td strong>Total</Td>
          <Td numeric strong>
            {formatCount(report.totals.won_deal_count)}
          </Td>
          <Td numeric strong>
            {formatCount(report.totals.lost_deal_count)}
          </Td>
          <Td numeric mono strong>
            {money(report.totals.won_first_year_our_share_minor)}
          </Td>
          <Td numeric mono strong>
            {money(report.totals.billed_our_share_minor)}
          </Td>
          <Td numeric mono strong>
            {money(report.totals.to_bill_minor)}
          </Td>
          <Td numeric mono strong>
            {money(report.totals.invoiced_minor)}
          </Td>
          <Td numeric mono strong>
            {money(report.totals.collected_minor)}
          </Td>
          {/* A level, not a flow: twelve months of run rate add up to nothing. */}
          <Td numeric muted>
            <span
              className={styles.notTotalled}
              title="A run rate is a level, not a flow — it is not totalled."
            >
              —
            </span>
          </Td>
          <Td numeric mono strong>
            {money(report.totals.cost_absorbed_minor)}
          </Td>
          <Td numeric mono strong>
            {money(report.totals.cost_recharged_minor)}
          </Td>
        </tr>
      </tfoot>
    </Table>
  );
}
